/**
 * Terms and Conditions API endpoints
 */
import { api } from './baseApi';

interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: T[];
}

export interface TermsAndConditions {
  id: number;
  title: string;
  terms: string;
  created_at: string;
  updated_at: string;
}

export interface TermsListItem {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

interface TermsResponse {
  success: boolean;
  data: TermsAndConditions;
  message?: string;
}

interface TermsListParams {
  search?: string;
  page?: number;
  page_size?: number;
}

export const termsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTerms: builder.query<PaginatedResponse<TermsListItem>, { page?: number; page_size?: number }>({
      query: (params) => ({
        url: '/terms/',
        params,
      }),
      providesTags: ['Terms'],
    }),
    getTermsDetail: builder.query<TermsResponse, number>({
      query: (id) => `/terms/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Terms', id }],
    }),
    getTermsAdmin: builder.query<PaginatedResponse<TermsListItem>, TermsListParams>({
      query: (params) => ({
        url: '/dashboard/terms/',
        params,
      }),
      providesTags: ['Terms'],
    }),
    getTermsAdminDetail: builder.query<TermsResponse, number>({
      query: (id) => `/dashboard/terms/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Terms', id }],
    }),
    createTerms: builder.mutation<TermsResponse, { title: string; terms: string }>({
      query: (body) => ({
        url: '/dashboard/terms/',
        method: 'POST',
        body: { title: body.title, terms: body.terms },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Terms'],
    }),
    updateTerms: builder.mutation<TermsResponse, { id: number; title: string; terms: string }>({
      query: ({ id, title, terms }) => ({
        url: `/dashboard/terms/${id}/`,
        method: 'PATCH',
        body: { title, terms },
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Terms', id }, 'Terms'],
    }),
    deleteTerms: builder.mutation<void, number>({
      query: (id) => ({
        url: `/dashboard/terms/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Terms'],
    }),
  }),
});

export const {
  useGetTermsQuery,
  useGetTermsDetailQuery,
  useGetTermsAdminQuery,
  useGetTermsAdminDetailQuery,
  useCreateTermsMutation,
  useUpdateTermsMutation,
  useDeleteTermsMutation,
} = termsApi;
